// Token Management Utility for PWA
class TokenManager {
  constructor() {
    this.checkInterval = 30 * 1000; // Check every 30 seconds
    this.intervalId = null;
    this.isMonitoring = false;
  }

  // Start monitoring token integrity
  startMonitoring() {
    if (this.isMonitoring) return;
    
    console.log('[TOKEN] Starting token integrity monitoring...');
    this.isMonitoring = true;
    
    // Initial check
    this.checkTokenIntegrity();
    
    // Periodic checks
    this.intervalId = setInterval(() => {
      this.checkTokenIntegrity();
    }, this.checkInterval);
  }

  // Stop monitoring
  stopMonitoring() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isMonitoring = false;
    console.log('[TOKEN] Token integrity monitoring stopped');
  }

  // Check and restore token integrity
  checkTokenIntegrity() {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.log('[TOKEN] Token missing, attempting recovery...');
        return this.attemptTokenRecovery();
      }
      
      // Validate token format (basic check)
      if (this.isValidTokenFormat(token)) {
        console.log('[TOKEN] Token integrity verified');
        return true;
      } else {
        console.warn('[TOKEN] Invalid token format, removing...');
        localStorage.removeItem('token');
        return false;
      }
    } catch (error) {
      console.error('[TOKEN] Error checking token integrity:', error);
      return false;
    }
  }

  // Attempt to recover token from backups
  attemptTokenRecovery() {
    let recovered = false;
    
    try {
      // Try emergency backup first
      const emergencyBackup = sessionStorage.getItem('__ewert_emergency_backup');
      if (emergencyBackup) {
        const backupData = JSON.parse(emergencyBackup);
        if (backupData.token && this.isValidTokenFormat(backupData.token)) {
          localStorage.setItem('token', backupData.token);
          sessionStorage.removeItem('__ewert_emergency_backup');
          console.log('[TOKEN] Token recovered from emergency backup');
          recovered = true;
        }
      }
      
      // Try session preserve
      if (!recovered) {
        const sessionPreserve = localStorage.getItem('__ewert_session_preserve');
        if (sessionPreserve) {
          const sessionData = JSON.parse(sessionPreserve);
          if (sessionData.token && this.isValidTokenFormat(sessionData.token)) {
            localStorage.setItem('token', sessionData.token);
            localStorage.removeItem('__ewert_session_preserve');
            console.log('[TOKEN] Token recovered from session preserve');
            recovered = true;
          }
        }
      }
      
      // Try push preserve
      if (!recovered) {
        const pushPreserve = localStorage.getItem('__ewert_push_preserve');
        if (pushPreserve) {
          const pushData = JSON.parse(pushPreserve);
          // Check if push data contains token info
          if (pushData.status && pushData.status.token) {
            localStorage.setItem('token', pushData.status.token);
            console.log('[TOKEN] Token recovered from push preserve');
            recovered = true;
          }
        }
      }
      
      if (recovered) {
        // Notify app about token recovery
        this.notifyTokenRecovery();
      }
      
      return recovered;
    } catch (error) {
      console.error('[TOKEN] Error during token recovery:', error);
      return false;
    }
  }

  // Basic token format validation
  isValidTokenFormat(token) {
    if (!token || typeof token !== 'string') return false;
    
    // Basic checks for JWT-like tokens
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    try {
      // Try to decode payload (basic validation)
      const payload = JSON.parse(atob(parts[1]));
      return payload && typeof payload === 'object';
    } catch (e) {
      return false;
    }
  }

  // Create emergency backup of current token
  createEmergencyBackup() {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const backupData = {
          token,
          timestamp: Date.now(),
          userAgent: navigator.userAgent
        };
        
        sessionStorage.setItem('__ewert_emergency_backup', JSON.stringify(backupData));
        console.log('[TOKEN] Emergency backup created');
      }
    } catch (error) {
      console.error('[TOKEN] Failed to create emergency backup:', error);
    }
  }

  // Notify app about token recovery
  notifyTokenRecovery() {
    try {
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('tokenRecovered', {
        detail: { timestamp: Date.now() }
      }));
      
      console.log('[TOKEN] Token recovery notification sent');
    } catch (error) {
      console.error('[TOKEN] Failed to notify about token recovery:', error);
    }
  }

  // Get token status
  getTokenStatus() {
    try {
      const token = localStorage.getItem('token');
      
      return {
        hasToken: !!token,
        isValid: token ? this.isValidTokenFormat(token) : false,
        hasEmergencyBackup: !!sessionStorage.getItem('__ewert_emergency_backup'),
        hasSessionPreserve: !!localStorage.getItem('__ewert_session_preserve'),
        timestamp: Date.now()
      };
    } catch (error) {
      console.error('[TOKEN] Error getting token status:', error);
      return {
        hasToken: false,
        isValid: false,
        error: error.message
      };
    }
  }

  // Cleanup old backups
  cleanupOldBackups() {
    try {
      // Clean emergency backup if older than 1 hour
      const emergencyBackup = sessionStorage.getItem('__ewert_emergency_backup');
      if (emergencyBackup) {
        const backupData = JSON.parse(emergencyBackup);
        if (Date.now() - backupData.timestamp > 60 * 60 * 1000) {
          sessionStorage.removeItem('__ewert_emergency_backup');
          console.log('[TOKEN] Cleaned up old emergency backup');
        }
      }
      
      // Clean session preserve if older than 30 minutes
      const sessionPreserve = localStorage.getItem('__ewert_session_preserve');
      if (sessionPreserve) {
        const sessionData = JSON.parse(sessionPreserve);
        if (Date.now() - sessionData.timestamp > 30 * 60 * 1000) {
          localStorage.removeItem('__ewert_session_preserve');
          console.log('[TOKEN] Cleaned up old session preserve');
        }
      }
    } catch (error) {
      console.error('[TOKEN] Error cleaning up old backups:', error);
    }
  }

  // Destroy token manager
  destroy() {
    this.stopMonitoring();
    console.log('[TOKEN] Token manager destroyed');
  }
}

export default TokenManager;
